import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../context/LanguageContext';
import { createOsmMap, keepMapSized } from '../utils/osmMap';
import './ActivityMap.css';

// Трек заезда на карте OpenStreetMap. Маркеры — кружки Leaflet, а не значки:
// картинки маркеров Leaflet сборка CRA по умолчанию теряет.
// position — [lat, lon] точки под курсором графика или null
const ActivityMap = ({ track, position }) => {
  const { t } = useLanguage();
  const nodeRef = useRef(null);
  const mapRef = useRef(null);
  const cursorRef = useRef(null);

  useEffect(() => {
    if (!nodeRef.current || !track || track.length < 2) return undefined;

    const map = createOsmMap(nodeRef.current, { scrollWheelZoom: false, zoomSnap: 0.25 });

    const line = L.polyline(track, { className: 'activity-map-line', weight: 4 }).addTo(map);
    L.circleMarker(track[0], { className: 'activity-map-start', radius: 7, weight: 3 })
      .bindTooltip(t('workouts.activity.start')).addTo(map);
    L.circleMarker(track[track.length - 1], { className: 'activity-map-finish', radius: 7, weight: 3 })
      .bindTooltip(t('workouts.activity.finish')).addTo(map);
    map.fitBounds(line.getBounds(), { padding: [24, 24] });

    // Колесо мыши масштабирует только после клика по карте, иначе страница не прокручивается
    map.on('click', () => map.scrollWheelZoom.enable());
    map.on('mouseout', () => map.scrollWheelZoom.disable());

    // Масштаб, выбранный пользователем, при смене ширины не сбрасываем
    const stopSizing = keepMapSized(map, nodeRef.current);

    mapRef.current = map;
    return () => {
      stopSizing();
      map.remove();
      mapRef.current = null;
      cursorRef.current = null;
    };
  }, [track, t]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!position) {
      cursorRef.current?.remove();
      cursorRef.current = null;
      return;
    }
    if (!cursorRef.current) {
      cursorRef.current = L.circleMarker(position, { className: 'activity-map-cursor', radius: 6, weight: 2 }).addTo(map);
    } else {
      cursorRef.current.setLatLng(position);
    }
  }, [position]);

  if (!track || track.length < 2) return null;

  return <div ref={nodeRef} className="activity-map" role="region" aria-label={t('workouts.activity.map')} />;
};

export default ActivityMap;
